import React from 'react';
import { useRef, useEffect } from 'react';
import { useSortBy, useTable, useFilters, useFlexLayout } from "react-table"
import * as S from "../../styles/ezev-td-styles/VehiclesTable-styles"
import { usePagination } from 'react-table';
import { useLocation, useHistory } from 'react-router-dom';

export const TABLE_STATE_KEY = 'vehiclesTableStateTd'; // sessionStorage key to save table state

export const defaultSortBy = [
  {
    id: "overall",
    desc: true
  },
  {
    id: 'years_remaining',
    desc: false
  },
  {
    id: "asset_id",
    desc: false
  }
];

export function calculateStickyLeft(columnId, columns) {
  let left = 0;
  for (let col of columns) {
    if (col.column) { // Handle case where column is nested for cell rendering
      col = col.column; 
    }
    if (col.id === columnId) {
      break;
    } 
    if (col.sticky) {
      left += col.width;
    } 
  }
  return left;
}

const VehiclesTable = ({ data, columns }) => {
  const location = useLocation();
  const history = useHistory();

  const tableRef = useRef(null);
  const userPageChangeRef = useRef(false);
  const isFirstLoad = useRef(true);

  // Retrieve previous table state if returning from single vcl page via browser or UI 
  let restoredState = {};
  try {
    const isReturning = (history && history.action === 'POP') || (location && location.state?.fromSingleVclPage);
    restoredState = isReturning 
      ? JSON.parse(sessionStorage.getItem(TABLE_STATE_KEY)) || {} 
      : {};
  } catch (error) {
    console.error("storageSession unavailable: ", error);
  }
  
  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    rows,
    gotoPage,

    state: { pageIndex, filters, sortBy },
  } = useTable({
    columns,
    data,
    initialState: {
      filters: restoredState.prevFilters ?? [],
      sortBy: restoredState.prevSortBy ?? defaultSortBy,
      pageIndex: 0,
      pageSize: 30
    }
  },
    useFlexLayout,
    useFilters,
    useSortBy,
    usePagination
  );

  const saveTableState = () => {
    try {
      sessionStorage.setItem(TABLE_STATE_KEY, JSON.stringify({
        prevScrollTop: tableRef.current.scrollTop,
        prevPageIndex: pageIndex,
        prevFilters: filters,
        prevSortBy: sortBy
      }));
    } catch (error) {
      console.error("storageSession unavailable: ", error);
    }
  };

  const handlePreviousPage = () => {
    userPageChangeRef.current = true;
    previousPage();
  };

  const handleNextPage = () => {
    userPageChangeRef.current = true;
    nextPage();
  }; 

  // Clear sessionStorage on page refresh
  useEffect(() => {
    if (isFirstLoad.current) {
      try {
        sessionStorage.removeItem(TABLE_STATE_KEY);
        isFirstLoad.current = false;
      } catch (error) {
        console.error("storageSession unavailable: ", error);
      }
    }
  }, [location.pathname]);

  // Restore previous table state
  useEffect(() => {
    if (restoredState.prevScrollTop) {
      tableRef.current.scrollTo({top: restoredState.prevScrollTop});
    }

    if (restoredState.prevPageIndex) {
      gotoPage(restoredState.prevPageIndex);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top of table on page change via pagination buttons
  useEffect(() => {
    if (!userPageChangeRef.current) return;

    tableRef.current.scrollTo({top:0});
  }, [pageIndex]);

  return (
    <>
      <S.StyledTable data-testid="vehiclesTableContainer-testid" {...getTableProps()} ref={tableRef}>
        <table data-testid="vehiclesTable-testid">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column =>
                (
                  <th 
                    className={column.sticky ? 'ezev-sticky-col' : ''}
                    {...column.getHeaderProps((props) => ({
                      style: {
                        ...props.style,
                        left: column.sticky ? calculateStickyLeft(column.id, headerGroup.headers) : props.style.left
                      }
                    }))} 
                  >
                    <div data-testid={`col-${column.id}-sortHeader-testid`} {...column.getSortByToggleProps()} className={`swt-sort-arrow  ${!column.canSort
                      ? ""
                      : column.isSorted
                        ? column.isSortedDesc
                          ? "ezev-desc"
                          : "ezev-asc"
                        : "ezev-both"}`}>
                      {column.render('Header')}

                    </div>
                    <S.SearchBar>{column.canFilter ? column.render('Filter') : null}</S.SearchBar>
                  </th>
                )
                )}
              </tr>
            ))}
          </thead>
          <tbody  {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} className={i % 2 === 0 ? "swt-ezio-even-row" : "swt-ezio-odd-row"}>
                  {row.cells.map(cell =>
                    (
                      <td 
                        data-testid={`row-${i}-${cell.column.testid}-testid`} 
                        className={`${cell.column.className ?? ""} ${cell.column.sticky ? 'ezev-sticky-col' : ''}`}
                        {...cell.getCellProps((props) => ({
                          style: {
                            ...props.style,
                            textDecoration: cell.column.id === "asset_id" ? "underline" : props.style.textDecoration,
                            color: cell.column.id === "asset_id" ? "var(--ezev-bluish)" : props.style.color,
                            left: cell.column.sticky ? calculateStickyLeft(cell.column.id, row.cells) : props.style.left     
                          }
                        }))}
                      >
                        {cell.render('Cell', { saveTableState })}
                      </td>
                    )
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>

      </S.StyledTable>
      {rows.length < 1 ?
      <S.NoDataMsg data-testid="noDataMessage-testid">There are no vehicles to display</S.NoDataMsg>
      :
      <S.PaginationContainer>
        <S.PaginationButtons onClick={handlePreviousPage} disabled={!canPreviousPage}>Previous</S.PaginationButtons>
        <S.PaginationDetails data-testid="paginationDetails-testid">Page {pageIndex + 1} of {pageOptions.length}</S.PaginationDetails>
        <S.PaginationButtons onClick={handleNextPage} disabled={!canNextPage} data-testid="vehiclesTableNextPageButton-testid">Next</S.PaginationButtons>
      </S.PaginationContainer>
      }
    </>
  )

}
export default VehiclesTable;
